import { ORGANIZATION_ID } from '../content/siteEntityIds.js';
import { authorReference } from './authorProfile.js';

const SITE_URL = 'https://bluebear.ai';

/* A reference to the one Organization node, defined in src/utils/siteEntityGraph.js and carried
 * by every document. Name, url and logo stay on it so the block still reads on its own, but the
 * `@id` is what makes an article's publisher the SAME company as the site's publisher rather
 * than a look-alike node repeated 45 times. */
function publisherReference() {
  return {
    '@type': 'Organization',
    '@id': ORGANIZATION_ID,
    name: 'BlueBear.ai',
    url: `${SITE_URL}/`,
    logo: { '@type': 'ImageObject', url: `${SITE_URL}/bluebear-logo.png` },
  };
}

export function absoluteArticleAssetUrl(value, fallback = '/bluebear-logo.png') {
  const asset = value || fallback;
  if (/^https?:\/\//.test(asset)) return asset;
  return `${SITE_URL}${asset.startsWith('/') ? asset : `/${asset}`}`;
}

export function buildVideoSchema({ title, summary, watchUrl, videoUrl, thumbnailUrl, uploadDate, duration, transcript }) {
  const canonicalWatchUrl = absoluteArticleAssetUrl(watchUrl, '/videos');
  return {
    '@context': 'https://schema.org',
    '@type': 'VideoObject',
    '@id': `${canonicalWatchUrl}#video`,
    name: title,
    description: summary,
    thumbnailUrl: [absoluteArticleAssetUrl(thumbnailUrl)],
    contentUrl: absoluteArticleAssetUrl(videoUrl),
    url: canonicalWatchUrl,
    ...(uploadDate ? { uploadDate } : {}),
    ...(duration ? { duration } : {}),
    ...(transcript ? { transcript } : {}),
    inLanguage: 'en-US',
    isFamilyFriendly: true,
    publisher: publisherReference(),
    potentialAction: { '@type': 'WatchAction', target: [canonicalWatchUrl] },
  };
}

export function buildArticleSchema({
  title,
  summary,
  url,
  image,
  datePublished,
  dateModified,
  articleSection = 'BlueBear Insights',
  citations = [],
  wordCount,
  keywords,
  videoUrl,
  videoPoster,
  videoCaption,
  videoDuration,
  videoTranscript,
}) {
  const canonicalUrl = absoluteArticleAssetUrl(url, '/');
  const imageUrl = absoluteArticleAssetUrl(image);
  const articleKeywords = keywords || `${title}, ${articleSection}, AI agents, MCP, AI infrastructure`;
  const videoContentUrl = videoUrl ? absoluteArticleAssetUrl(videoUrl) : null;

  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    '@id': `${canonicalUrl}#article`,
    url: canonicalUrl,
    mainEntityOfPage: { '@type': 'WebPage', '@id': canonicalUrl },
    headline: title,
    name: title,
    description: summary,
    abstract: summary,
    image: imageUrl,
    inLanguage: 'en-US',
    isAccessibleForFree: true,
    keywords: articleKeywords,
    about: [{ '@type': 'Thing', name: articleSection }],
    ...(datePublished ? { datePublished } : {}),
    ...(dateModified || datePublished ? { dateModified: dateModified || datePublished } : {}),
    articleSection,
    ...(wordCount ? { wordCount } : {}),
    ...(citations.length ? { citation: citations } : {}),
    /* One Person node, referenced by `@id` from all 45 articles, rather than an
     * author string repeated 45 times. The full node lives on /phillip-joe; this is
     * the reference to it. The Organization stays where it belongs — `publisher`. */
    author: authorReference(),
    publisher: publisherReference(),
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_URL}/` },
        { '@type': 'ListItem', position: 2, name: 'Insights', item: `${SITE_URL}/insights` },
        { '@type': 'ListItem', position: 3, name: title, item: canonicalUrl },
      ],
    },
    speakable: {
      '@type': 'SpeakableSpecification',
      cssSelector: ['.article-answer-summary', '.article-content h2', '.article-content h3'],
    },
    potentialAction: { '@type': 'ReadAction', target: [canonicalUrl] },
    ...(videoContentUrl ? {
      video: {
        '@type': 'VideoObject',
        name: `${title} visual field guide`,
        description: videoCaption || summary,
        ...(videoCaption ? { caption: videoCaption } : {}),
        ...(videoTranscript ? { transcript: videoTranscript } : {}),
        thumbnailUrl: absoluteArticleAssetUrl(videoPoster || image),
        contentUrl: videoContentUrl,
        ...(datePublished ? { uploadDate: datePublished } : {}),
        ...(videoDuration ? { duration: videoDuration } : {}),
      },
    } : {}),
  };
}
